import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Loader2, AlertCircle, Phone, Globe, Lock, ChevronDown, ChevronRight, Check } from 'lucide-react';
// import './Integration.css'; // Removed, using Tailwind

// Meta App ID from environment (injected via build process)
const META_APP_ID = process.env.REACT_APP_WHATSAPP_APP_ID || '';
const META_CONFIG_ID = process.env.REACT_APP_WHATSAPP_CONFIG_ID || '';

/**
 * WhatsApp Business Integration component
 * Allows users to connect their WhatsApp Business account to the chatbot
 */
function WhatsAppIntegration({
    whatsAppEnabled,
    setWhatsAppEnabled,
    whatsAppPhoneNumber,
    setWhatsAppPhoneNumber,
    whatsAppPhoneNumberId,
    setWhatsAppPhoneNumberId,
    whatsAppBusinessAccountId,
    setWhatsAppBusinessAccountId,
    whatsAppDisplayName,
    setWhatsAppDisplayName,
    whatsAppAccessToken,
    setWhatsAppAccessToken,
    persistAndUpdate,
    chatbotId
}) {
    const [isConnecting, setIsConnecting] = useState(false);
    const [connectionError, setConnectionError] = useState('');
    const [isConnected, setIsConnected] = useState(false);

    // Legacy/Manual configuration state
    const [showLegacyConfig, setShowLegacyConfig] = useState(false);
    const [manualWabaId, setManualWabaId] = useState(whatsAppBusinessAccountId || '');
    const [manualPhoneNumberId, setManualPhoneNumberId] = useState(whatsAppPhoneNumberId || '');
    const [manualAccessToken, setManualAccessToken] = useState(whatsAppAccessToken || '');
    const [manualPhoneNumber, setManualPhoneNumber] = useState(whatsAppPhoneNumber || '');
    const [manualDisplayName, setManualDisplayName] = useState(whatsAppDisplayName || '');

    // Check if WhatsApp is connected
    useEffect(() => {
        setIsConnected(whatsAppEnabled && whatsAppPhoneNumber);
    }, [whatsAppEnabled, whatsAppPhoneNumber]);

    // Promise-based FB SDK initialization
    const ensureFBReady = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (window.__FB_INIT_DONE__ === META_APP_ID) {
                console.log('FB SDK already initialized with our app ID');
                resolve();
                return;
            }

            if (!window.FB) {
                reject(new Error('Facebook SDK not loaded'));
                return;
            }

            console.log('Initializing FB SDK...');
            window.FB.init({
                appId: META_APP_ID,
                cookie: true,
                xfbml: true,
                version: 'v18.0'
            });

            setTimeout(() => {
                window.FB.getLoginStatus((response) => {
                    console.log('FB SDK init verified, status:', response.status);
                    window.__FB_INIT_DONE__ = META_APP_ID;
                    resolve();
                });
            }, 100);
        });
    }, []);

    // Load FB SDK script
    useEffect(() => {
        if (!META_APP_ID) {
            console.error('MISSING APP ID: process.env.REACT_APP_WHATSAPP_APP_ID is not set.');
            return;
        }

        const originalFbAsyncInit = window.fbAsyncInit;
        window.fbAsyncInit = function () {
            if (originalFbAsyncInit) {
                originalFbAsyncInit();
            }
            console.log('FB SDK loaded (fbAsyncInit)');
        };

        if (!document.getElementById('facebook-jssdk')) {
            const script = document.createElement('script');
            script.id = 'facebook-jssdk';
            script.src = 'https://connect.facebook.net/en_US/sdk.js';
            script.async = true;
            script.defer = true;
            script.crossOrigin = 'anonymous';
            script.onerror = () => {
                console.error('Failed to load Facebook SDK. It might be blocked by an ad blocker.');
                setConnectionError('Facebook SDK blocked. Please disable AdBlock or Privacy extensions.');
            };
            document.body.appendChild(script);
        }
    }, []);

    // Get WhatsApp Business Account info from Graph API
    const getWhatsAppBusinessInfo = (accessToken) => {
        return new Promise((resolve, reject) => {
            window.FB.api(
                '/me/whatsapp_business_accounts',
                { access_token: accessToken },
                (response) => {
                    if (response.error) {
                        reject(new Error(response.error.message));
                        return;
                    }
                    if (!response.data || response.data.length === 0) {
                        reject(new Error('No WhatsApp Business Account found'));
                        return;
                    }

                    const waba = response.data[0];

                    window.FB.api(
                        `/${waba.id}/phone_numbers`,
                        { access_token: accessToken },
                        (phoneResponse) => {
                            if (phoneResponse.error) {
                                reject(new Error(phoneResponse.error.message));
                                return;
                            }
                            if (!phoneResponse.data || phoneResponse.data.length === 0) {
                                reject(new Error('No phone numbers found for WhatsApp Business Account'));
                                return;
                            }
                            const phone = phoneResponse.data[0];
                            resolve({
                                wabaId: waba.id,
                                phoneNumberId: phone.id,
                                phoneNumber: phone.display_phone_number,
                                displayName: phone.verified_name || phone.display_phone_number
                            });
                        }
                    );
                }
            );
        });
    };

    // Handle OAuth callback from Meta
    const handleOAuthCallback = useCallback(async (authResponse) => {
        try {
            const { code } = authResponse;
            const wabaInfo = await getWhatsAppBusinessInfo(authResponse.accessToken);

            const response = await fetch('/api/whatsapp/connect', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    chatbotId: chatbotId,
                    code: code,
                    phoneNumberId: wabaInfo.phoneNumberId,
                    wabaId: wabaInfo.wabaId,
                    phoneNumber: wabaInfo.phoneNumber,
                    displayName: wabaInfo.displayName
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to connect WhatsApp');
            }

            const result = await response.json();

            setWhatsAppEnabled(true);
            setWhatsAppPhoneNumber(result.phoneNumber);
            setWhatsAppDisplayName(result.displayName);
            setWhatsAppPhoneNumberId(wabaInfo.phoneNumberId);
            setWhatsAppBusinessAccountId(wabaInfo.wabaId);

            persistAndUpdate({
                whatsAppEnabled: true,
                whatsAppPhoneNumber: result.phoneNumber,
                whatsAppPhoneNumberId: wabaInfo.phoneNumberId,
                whatsAppBusinessAccountId: wabaInfo.wabaId,
                whatsAppDisplayName: result.displayName
            });

            setIsConnected(true);
            setIsConnecting(false);

        } catch (error) {
            console.error('WhatsApp connection error:', error);
            setConnectionError(error.message || 'Failed to connect WhatsApp');
            setIsConnecting(false);
        }
    }, [chatbotId, persistAndUpdate, setWhatsAppEnabled, setWhatsAppPhoneNumber, setWhatsAppDisplayName, setWhatsAppPhoneNumberId, setWhatsAppBusinessAccountId]);

    // Handle WhatsApp connect button click
    const handleConnect = useCallback(async () => {
        if (!META_APP_ID) {
            setConnectionError('WhatsApp integration is not configured. Please contact support.');
            return;
        }
        if (!window.FB) {
            setConnectionError('Facebook SDK is not loaded. Please disable AdBlockers and refresh.');
            return;
        }

        setIsConnecting(true);
        setConnectionError('');

        try {
            await ensureFBReady();
            const loginOptions = {
                scope: 'whatsapp_business_management,whatsapp_business_messaging'
            };
            if (META_CONFIG_ID) {
                loginOptions.config_id = META_CONFIG_ID;
            }

            window.FB.login(
                (response) => {
                    if (response.authResponse) {
                        handleOAuthCallback(response.authResponse);
                    } else {
                        setIsConnecting(false);
                        setConnectionError('Authorization cancelled or failed.');
                    }
                },
                loginOptions
            );
        } catch (error) {
            console.error('FB SDK initialization error:', error);
            setConnectionError(error.message || 'Failed to initialize Facebook SDK');
            setIsConnecting(false);
        }
    }, [handleOAuthCallback, ensureFBReady]);

    // Handle disconnect
    const handleDisconnect = useCallback(async () => {
        if (!window.confirm('Are you sure you want to disconnect WhatsApp? This will stop the chatbot from responding to WhatsApp messages.')) {
            return;
        }

        try {
            const response = await fetch('/api/whatsapp/disconnect', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ chatbotId: chatbotId })
            });

            if (!response.ok) {
                throw new Error('Failed to disconnect WhatsApp');
            }

            setWhatsAppEnabled(false);
            setWhatsAppPhoneNumber('');
            setWhatsAppPhoneNumberId('');
            setWhatsAppBusinessAccountId('');
            setWhatsAppDisplayName('');
            setWhatsAppAccessToken('');

            persistAndUpdate({
                whatsAppEnabled: false,
                whatsAppPhoneNumber: '',
                whatsAppPhoneNumberId: '',
                whatsAppBusinessAccountId: '',
                whatsAppDisplayName: '',
                whatsAppAccessToken: ''
            });

            setIsConnected(false);

        } catch (error) {
            console.error('WhatsApp disconnect error:', error);
            setConnectionError('Failed to disconnect WhatsApp');
        }
    }, [chatbotId, persistAndUpdate, setWhatsAppEnabled, setWhatsAppPhoneNumber, setWhatsAppPhoneNumberId, setWhatsAppBusinessAccountId, setWhatsAppDisplayName, setWhatsAppAccessToken]);

    // Handle manual enable/disable toggle
    const handleToggle = useCallback((e) => {
        const enabled = e.target.checked;
        if (enabled && !isConnected) {
            handleConnect();
        } else if (!enabled && isConnected) {
            handleDisconnect();
        }
    }, [isConnected, handleConnect, handleDisconnect]);

    // Handle manual/legacy configuration save
    const handleManualSave = useCallback(async () => {
        if (!manualWabaId || !manualPhoneNumberId || !manualAccessToken) {
            setConnectionError('Please fill in all required fields (WABA ID, Phone Number ID, and Access Token)');
            return;
        }

        setIsConnecting(true);
        setConnectionError('');

        try {
            const response = await fetch('/api/whatsapp/connect', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    chatbotId: chatbotId,
                    phoneNumberId: manualPhoneNumberId,
                    wabaId: manualWabaId,
                    phoneNumber: manualPhoneNumber || 'Manual Config',
                    displayName: manualDisplayName || 'WhatsApp Business',
                    accessToken: manualAccessToken,
                    isManualConfig: true
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to save WhatsApp configuration');
            }

            setWhatsAppEnabled(true);
            setWhatsAppPhoneNumber(manualPhoneNumber || 'Manual Config');
            setWhatsAppDisplayName(manualDisplayName || 'WhatsApp Business');
            setWhatsAppPhoneNumberId(manualPhoneNumberId);
            setWhatsAppBusinessAccountId(manualWabaId);
            setWhatsAppAccessToken(manualAccessToken);

            persistAndUpdate({
                whatsAppEnabled: true,
                whatsAppPhoneNumber: manualPhoneNumber || 'Manual Config',
                whatsAppPhoneNumberId: manualPhoneNumberId,
                whatsAppBusinessAccountId: manualWabaId,
                whatsAppDisplayName: manualDisplayName || 'WhatsApp Business',
                whatsAppAccessToken: manualAccessToken
            });

            setIsConnected(true);
            setIsConnecting(false);
            setShowLegacyConfig(false);

        } catch (error) {
            console.error('Manual config save error:', error);
            setConnectionError(error.message || 'Failed to save configuration');
            setIsConnecting(false);
        }
    }, [chatbotId, manualWabaId, manualPhoneNumberId, manualAccessToken, manualPhoneNumber, manualDisplayName, persistAndUpdate, setWhatsAppEnabled, setWhatsAppPhoneNumber, setWhatsAppDisplayName, setWhatsAppPhoneNumberId, setWhatsAppBusinessAccountId, setWhatsAppAccessToken]);

    return (
        <div className="space-y-6">


            {/* Connection Status Card */}
            {isConnected && (
                <div className="bg-green-50 border border-green-100 rounded-xl p-5 shadow-sm">
                    <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 text-green-600">
                            <Check size={20} className="stroke-[3]" />
                        </div>
                        <div className="flex-1">
                            <h4 className="text-base font-bold text-green-800 mb-1">WhatsApp Connected</h4>
                            <p className="text-sm text-green-700 mb-4">
                                Your chatbot is active and responding to messages on WhatsApp.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white/60 rounded-lg p-3 border border-green-100">
                                    <label className="text-xs font-semibold uppercase tracking-wider text-green-600 block mb-1">
                                        Phone Number
                                    </label>
                                    <div className="font-medium text-green-900">{whatsAppPhoneNumber}</div>
                                </div>
                                {whatsAppDisplayName && (
                                    <div className="bg-white/60 rounded-lg p-3 border border-green-100">
                                        <label className="text-xs font-semibold uppercase tracking-wider text-green-600 block mb-1">
                                            Display Name
                                        </label>
                                        <div className="font-medium text-green-900">{whatsAppDisplayName}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {connectionError && (
                <div className="bg-red-50 border border-red-100 rounded-lg p-4 flex items-start gap-3">
                    <AlertCircle className="text-red-500 w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div>
                        <h4 className="text-sm font-bold text-red-800">Connection Error</h4>
                        <p className="text-sm text-red-600 mt-1">{connectionError}</p>
                    </div>
                </div>
            )}

            {/* Main Action Area */}
            <div className={`p-6 bg-slate-50 rounded-2xl border border-slate-200 transition-all duration-300 ${isConnected ? 'opacity-70 pointer-events-none grayscale-[50%]' : ''}`}>
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h4 className="font-bold text-slate-800 text-lg">Connect Account</h4>
                        <p className="text-sm text-slate-500">Link your WhatsApp Business Profile</p>
                    </div>
                    {/* Toggle Switch */}
                    <div className="flex items-center gap-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={whatsAppEnabled}
                                onChange={handleToggle}
                                disabled={isConnecting}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                            <span className="ml-3 text-sm font-medium text-slate-700">
                                {whatsAppEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                        </label>
                    </div>
                </div>

                {!isConnected ? (
                    <div className="mt-4">
                        <button
                            type="button"
                            onClick={handleConnect}
                            disabled={isConnecting || !META_APP_ID}
                            className="w-full flex items-center justify-center gap-2 py-3 bg-[#25D366] hover:bg-[#1faa53] text-white rounded-xl font-bold transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isConnecting ? (
                                <>
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Connecting...
                                </>
                            ) : (
                                <>
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                    </svg>
                                    Connect WhatsApp Business
                                </>
                            )}
                        </button>
                        {!META_APP_ID && (
                            <p className="text-xs text-red-500 mt-2 text-center">
                                Missing WhatsApp App ID configuration.
                            </p>
                        )}
                    </div>
                ) : (
                    <button
                        type="button"
                        onClick={handleDisconnect}
                        className="w-full py-3 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl font-bold transition-all border border-red-100 mt-4 pointer-events-auto"
                    >
                        Disconnect WhatsApp
                    </button>
                )}
            </div>

            {/* Manual Config Toggle */}
            {!isConnected && (
                <div className="pt-2">
                    <button
                        type="button"
                        onClick={() => setShowLegacyConfig(!showLegacyConfig)}
                        className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800 transition-colors mx-auto font-medium"
                    >
                        {showLegacyConfig ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        Advanced: Manual Configuration
                    </button>

                    {showLegacyConfig && (
                        <div className="mt-4 p-5 bg-white border border-slate-200 rounded-xl shadow-sm">
                            <h4 className="text-sm font-bold text-slate-800 mb-4 border-b border-slate-100 pb-2">
                                Manual Connection Details
                            </h4>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                                        WABA ID <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <Globe className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
                                        <input
                                            type="text"
                                            value={manualWabaId}
                                            onChange={(e) => setManualWabaId(e.target.value)}
                                            placeholder="e.g. 861402252960691"
                                            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium placeholder:font-normal"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                                        Phone Number ID <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
                                        <input
                                            type="text"
                                            value={manualPhoneNumberId}
                                            onChange={(e) => setManualPhoneNumberId(e.target.value)}
                                            placeholder="e.g. 885982924604802"
                                            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium placeholder:font-normal"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                                        Access Token <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <Lock className="absolute left-3 top-2.5 text-slate-400 w-4 h-4" />
                                        <input
                                            type="password"
                                            value={manualAccessToken}
                                            onChange={(e) => setManualAccessToken(e.target.value)}
                                            placeholder="••••••••••••••••"
                                            className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium placeholder:font-normal"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                                            Phone Number (Optional)
                                        </label>
                                        <input
                                            type="text"
                                            value={manualPhoneNumber}
                                            onChange={(e) => setManualPhoneNumber(e.target.value)}
                                            placeholder="+1 234 567 8900"
                                            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium placeholder:font-normal"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                                            Display Name (Optional)
                                        </label>
                                        <input
                                            type="text"
                                            value={manualDisplayName}
                                            onChange={(e) => setManualDisplayName(e.target.value)}
                                            placeholder="My Business"
                                            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium placeholder:font-normal"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={handleManualSave}
                                    disabled={isConnecting}
                                    className="w-full mt-2 py-2.5 bg-slate-800 text-white rounded-lg text-sm font-semibold hover:bg-slate-700 transition-all shadow-sm"
                                >
                                    {isConnecting ? 'Saving...' : 'Save Configuration'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Informational Footer */}
            {!isConnected && (
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-5">
                    <h4 className="text-sm font-bold text-blue-800 mb-2">How it works</h4>
                    <ul className="space-y-2 text-sm text-blue-700">
                        <li className="flex items-start gap-2">
                            <span className="bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold mt-0.5 flex-shrink-0">1</span>
                            <span>Click "Connect WhatsApp Business" to launch the Meta login flow.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold mt-0.5 flex-shrink-0">2</span>
                            <span>Select your Business Account and the Phone Number you want to use.</span>
                        </li>
                        <li className="flex items-start gap-2">
                            <span className="bg-blue-200 text-blue-800 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold mt-0.5 flex-shrink-0">3</span>
                            <span>Your chatbot will instantly start handling conversations on that number.</span>
                        </li>
                    </ul>
                </div>
            )}
        </div>
    );
}

WhatsAppIntegration.propTypes = {
    whatsAppEnabled: PropTypes.bool.isRequired,
    setWhatsAppEnabled: PropTypes.func.isRequired,
    whatsAppPhoneNumber: PropTypes.string,
    setWhatsAppPhoneNumber: PropTypes.func.isRequired,
    whatsAppPhoneNumberId: PropTypes.string,
    setWhatsAppPhoneNumberId: PropTypes.func.isRequired,
    whatsAppBusinessAccountId: PropTypes.string,
    setWhatsAppBusinessAccountId: PropTypes.func.isRequired,
    whatsAppDisplayName: PropTypes.string,
    setWhatsAppDisplayName: PropTypes.func.isRequired,
    whatsAppAccessToken: PropTypes.string,
    setWhatsAppAccessToken: PropTypes.func.isRequired,
    persistAndUpdate: PropTypes.func.isRequired,
    chatbotId: PropTypes.string.isRequired
};

WhatsAppIntegration.defaultProps = {
    whatsAppPhoneNumber: '',
    whatsAppPhoneNumberId: '',
    whatsAppBusinessAccountId: '',
    whatsAppDisplayName: '',
    whatsAppAccessToken: ''
};

export default WhatsAppIntegration;
